<script lang="ts">
  import { untrack } from 'svelte';
  import { store } from '$lib/store.svelte';
  import { TRANSICIONES, TIPOS_DESTINO, IdeaError, type Idea, type PatchIdea, type TipoDestino } from '$lib/idea';
  import { formatDate } from '$lib/gtd';

  interface Props { idea: Idea; onClose: () => void; onError: (m: string) => void }
  let { idea, onClose, onError }: Props = $props();

  const parseTags = (t: string) => t.split(',').map((x) => x.trim()).filter(Boolean);

  // Valores con los que se abrió (o se re-basó) la edición: solo se guarda lo que cambió
  // respecto de esto, con `version` para detectar ediciones concurrentes.
  let base = untrack(() => ({ texto: idea.texto, notas: idea.notas, tags: idea.tags.join(', ') }));
  let version = untrack(() => idea.updatedAt);
  let texto = $state(base.texto);
  let notas = $state(base.notas);
  let tags = $state(base.tags);
  let tipo = $state<TipoDestino>('proyecto');
  let ref = $state('');

  const actual = $derived(store.ideas.find((i) => i.id === idea.id) ?? idea);
  const RESTAURABLES = ['texto', 'notas', 'tags', 'relacionadas', 'estado'];

  /** Tras una acción propia en este modal: nueva versión; los campos no tocados siguen al store. */
  function rebase() {
    const i = store.ideas.find((x) => x.id === idea.id);
    if (!i) return;
    const nuevo = { texto: i.texto, notas: i.notas, tags: i.tags.join(', ') };
    if (texto === base.texto) texto = nuevo.texto;
    if (notas === base.notas) notas = nuevo.notas;
    if (parseTags(tags).join(',') === parseTags(base.tags).join(',')) tags = nuevo.tags;
    base = nuevo;
    version = i.updatedAt;
  }

  $effect(() => {
    if (idea.pendienteRevision) void store.marcarRevisadoIdea(idea.id).then(rebase, () => {});
  });

  async function act(fn: () => Promise<unknown>, cerrar = false) {
    try {
      await fn();
      if (cerrar) onClose();
      else rebase();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  }

  async function guardar() {
    const patch: PatchIdea = {};
    if (texto !== base.texto) patch.texto = texto;
    if (notas !== base.notas) patch.notas = notas;
    const lista = parseTags(tags);
    if (lista.join(',') !== parseTags(base.tags).join(',')) patch.tags = lista;
    if (Object.keys(patch).length === 0) return onClose();
    try {
      await store.editarIdea(idea.id, patch, version);
      onClose();
    } catch (e) {
      if (e instanceof IdeaError && e.code === 'conflict') onError('La idea cambió mientras la editabas; cerrá y volvé a abrir');
      else onError(e instanceof Error ? e.message : String(e));
    }
  }
</script>

<div class="modal-backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && onClose()}>
<div class="modal" role="dialog" aria-modal="true">
  <div class="modal-header"><div class="modal-title">Idea · {actual.estado}</div></div>

  <div class="form-group">
    <label class="form-label" for="i-texto">Idea</label>
    <input id="i-texto" class="form-input" bind:value={texto} />
  </div>
  <div class="form-group">
    <label class="form-label" for="i-notas">Notas</label>
    <textarea id="i-notas" class="form-textarea" rows="5" bind:value={notas}></textarea>
  </div>
  <div class="form-group">
    <label class="form-label" for="i-tags">Tags (separados por coma)</label>
    <input id="i-tags" class="form-input" bind:value={tags} />
  </div>

  <div class="form-group">
    <span class="form-label">Estado</span>
    {#each TRANSICIONES[actual.estado] as e}
      <button type="button" class="btn btn-secondary" onclick={() => act(() => store.cambiarEstadoIdea(idea.id, e))}>→ {e}</button>
    {/each}
  </div>

  {#if actual.propuesta}
    <div class="item" style="display:block">
      <strong>Propuesta de {actual.propuesta.autor}:</strong> {actual.propuesta.tipo}
      {actual.propuesta.ref ? `· ${actual.propuesta.ref}` : ''} — {actual.propuesta.motivo}
      <div style="margin-top:var(--space-xs)">
        <button type="button" class="btn btn-primary" onclick={() => act(() => store.aceptarPropuesta(idea.id))}>Aceptar y promover</button>
        <button type="button" class="btn btn-secondary" onclick={() => act(() => store.rechazarPropuestaIdea(idea.id))}>Rechazar</button>
      </div>
    </div>
  {:else if actual.estado === 'capturada' || actual.estado === 'madurando'}
    <div class="form-group">
      <span class="form-label">Promover</span>
      <select class="form-input" bind:value={tipo}>
        {#each TIPOS_DESTINO as t}<option value={t}>{t}</option>{/each}
      </select>
      <input class="form-input" placeholder="Referencia (slug, proyecto…)" bind:value={ref} />
      <button type="button" class="btn btn-primary" onclick={() => act(() => store.promoverIdea(idea.id, { tipo, ref }))}>Promover</button>
    </div>
  {/if}

  <div class="form-group">
    <span class="form-label">Historial</span>
    {#each actual.historial as h, i}
      <div class="item-meta" style="display:flex;gap:var(--space-xs);align-items:center">
        <span>{formatDate(h.fecha)} · {h.autor} · {h.accion}{h.campo && h.campo !== h.accion ? ` ${h.campo}` : ''}{h.motivo ? ` — ${h.motivo}` : ''}</span>
        {#if RESTAURABLES.includes(h.campo) && !(h.campo === 'estado' && (h.anterior === 'promovida' || actual.estado === 'promovida'))}
          <button type="button" class="icon-btn" title="Restaurar valor anterior" onclick={() => act(() => store.restaurarIdea(idea.id, i))}>↺</button>
        {/if}
      </div>
    {/each}
  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-danger" onclick={() => confirm('¿Borrar la idea?') && act(() => store.borrarIdea(idea.id), true)}>Borrar</button>
    <button type="button" class="btn btn-secondary" onclick={onClose}>Cerrar</button>
    <button type="button" class="btn btn-primary" onclick={guardar}>Guardar</button>
  </div>
</div>
</div>
